import { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import API from '../api';
import toast from 'react-hot-toast';
import {
  FiCheck,
  FiX,
  FiClock,
  FiRotateCcw,
  FiCreditCard,
  FiLink,
  FiCamera,
  FiDownload,
  FiRefreshCw,
  FiEye,
  FiExternalLink,
  FiRotateCw,
  FiMail,
  FiCalendar,
} from 'react-icons/fi';

const StatusBadge = ({ status }) => {
  const map   = { SUCCESS: 'badge-success', FAILED: 'badge-failed', PENDING: 'badge-pending', REFUNDED: 'badge-refunded' };
  const icons = { SUCCESS: <FiCheck />, FAILED: <FiX />, PENDING: <FiClock />, REFUNDED: <FiRotateCcw /> };
  return (
    <span className={`badge ${map[status] || 'badge-pending'}`}>
      {icons[status]} {status}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const icons = { DIRECT: <FiCreditCard />, LINK: <FiLink />, QR: <FiCamera /> };
  return (
    <span style={{
      fontSize: 12, color: 'var(--text3)', background: 'var(--bg3)',
      padding: '2px 8px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      {icons[type]} {type}
    </span>
  );
};

// Email pill: shows sent / not sent / N/A
const EmailPill = ({ emailSent, hasEmail }) => {
  if (!hasEmail) {
    return (
      <span style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>—</span>
    );
  }
  return emailSent ? (
    <span style={{
      fontSize: 11, color: 'var(--success)',
      background: 'rgba(0,214,143,0.12)',
      padding: '2px 8px', borderRadius: 20,
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      <FiMail /> Sent
    </span>
  ) : (
    <span style={{
      fontSize: 11, color: 'var(--text3)',
      background: 'var(--bg3)',
      padding: '2px 8px', borderRadius: 20,
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      <FiMail /> Not sent
    </span>
  );
};

export default function Transactions() {
  const [txns,     setTxns]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);

  const [filters, setFilters] = useState({
    page: 1, limit: 15,
    search: '', status: '', type: '',
    startDate: '', endDate: '', eventName: '',
  });

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  const fetchTxns = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await API.get('/payment/transactions', { params });
      setTxns(data.transactions || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchTxns(); }, [fetchTxns]);

  const checkStatus = async (txnId) => {
    toast.loading('Checking status...', { id: 'status' });
    try {
      const { data } = await API.get(`/payment/status/${txnId}`);
      toast.success(`Status: ${data.status}`, { id: 'status' });
      fetchTxns();
    } catch {
      toast.error('Status check failed', { id: 'status' });
    }
  };

  const exportCSV = () => {
    const headers = ['ID', 'Transaction ID', 'PhonePe ID', 'Customer', 'Phone', 'Email', 'Amount', 'Event', 'Type', 'Status', 'Email Sent','Created By', 'Date'];
    const rows = txns.map(t => [
      t.id,
      t.merchant_transaction_id,
      t.phonepe_transaction_id || '',
      t.customer_name,
      t.customer_phone,
      t.customer_email || '',
      t.amount,
      t.event_name || '',
      t.payment_type,
      t.status,
      t.email_sent ? 'Yes' : (t.customer_email ? 'No' : 'N/A'),
      t.created_by_username || '',   // ← add
      new Date(t.created_at).toLocaleString('en-IN'),
    ]);
    const csv  = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `SteponePay-transactions-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV exported!');
  };

  const resetFilters = () => setFilters({ page: 1, limit: 15, search: '', status: '', type: '', startDate: '', endDate: '', eventName: '' });

  return (
    <Layout>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Transactions</h1>
            <p className="page-subtitle">{total} total records found</p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={exportCSV}>
            <FiDownload style={{ marginRight: 6 }} /> Export CSV
          </button>
        </div>
      </div>

      <div className="page-body fade-in">
        {/* ── Filters ── */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <div>
              <label className="form-label">Search</label>
              <input className="form-input" placeholder="Name, phone, txn ID, event..." value={filters.search}
                onChange={e => setF('search', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Status</label>
              <select className="form-select" value={filters.status} onChange={e => setF('status', e.target.value)}>
                <option value="">All Status</option>
                <option value="SUCCESS">Success</option>
                <option value="PENDING">Pending</option>
                <option value="FAILED">Failed</option>
                <option value="REFUNDED">Refunded</option>
              </select>
            </div>
            <div>
              <label className="form-label">Payment Type</label>
              <select className="form-select" value={filters.type} onChange={e => setF('type', e.target.value)}>
                <option value="">All Types</option>
                <option value="DIRECT">Direct Pay</option>
                <option value="LINK">Payment Link</option>
                <option value="QR">QR Code</option>
              </select>
            </div>
            <div>
              <label className="form-label">
                <FiCalendar style={{ marginRight: 4, verticalAlign: 'middle' }} />Event Name
              </label>
              <input className="form-input" placeholder="Filter by event..." value={filters.eventName}
                onChange={e => setF('eventName', e.target.value)} />
            </div>
            <div>
              <label className="form-label">From Date</label>
              <input className="form-input" type="date" value={filters.startDate}
                onChange={e => setF('startDate', e.target.value)} />
            </div>
            <div>
              <label className="form-label">To Date</label>
              <input className="form-input" type="date" value={filters.endDate}
                onChange={e => setF('endDate', e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={resetFilters}>
                <FiRefreshCw style={{ marginRight: 6 }} /> Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <span className="spinner" style={{ borderTopColor: 'var(--primary)', width: 28, height: 28, borderWidth: 3 }} />
              <p style={{ color: 'var(--text3)', marginTop: 12, fontSize: 14 }}>Loading transactions...</p>
            </div>
          ) : txns.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <p>No transactions found matching your filters</p>
            </div>
          ) : (
            <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Transaction ID</th>
                    <th>Customer</th>
                    <th>Event</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <FiMail /> Email
                      </span>
                    </th>
                    <th>Date</th>
                    <th>Created By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((txn, i) => (
                    <tr key={txn.id}>
                      <td style={{ color: 'var(--text3)', fontSize: 12 }}>
                        {(filters.page - 1) * filters.limit + i + 1}
                      </td>
                      <td>
                        <span className="font-mono" style={{ fontSize: 11 }}>{txn.merchant_transaction_id}</span>
                        {txn.phonepe_transaction_id && (
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                            PG: {txn.phonepe_transaction_id}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500, color: 'var(--text)' }}>{txn.customer_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{txn.customer_phone}</div>
                        {txn.customer_email && (
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{txn.customer_email}</div>
                        )}
                      </td>
                      <td>
                        {txn.event_name
                          ? (
                            <span style={{
                              fontSize: 12, color: 'var(--primary)',
                              background: 'rgba(124,92,252,0.1)',
                              padding: '2px 8px', borderRadius: 4,
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                            title={txn.event_name}>
                              🎫 {txn.event_name}
                            </span>
                          )
                          : <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>
                        }
                      </td>
                      <td>
                        <span style={{
                          fontWeight: 700,
                          color: txn.status === 'SUCCESS' ? 'var(--success)' : 'var(--text)',
                          fontSize: 15,
                        }}>
                          ₹{parseFloat(txn.amount).toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td><TypeBadge type={txn.payment_type} /></td>
                      <td>
                        <StatusBadge status={txn.status} />
                        {txn.webhook_verified === 1 && (
                          <div style={{ fontSize: 10, color: 'var(--success)', marginTop: 3 }}>✓ Webhook verified</div>
                        )}
                      </td>
                      <td>
                        <EmailPill emailSent={txn.email_sent === 1} hasEmail={!!txn.customer_email} />
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                        {new Date(txn.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td>
  {txn.created_by_username ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
        background: 'rgba(124,92,252,0.15)', color: 'var(--primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, fontWeight: 700,
      }}>
        {(txn.created_by_name || txn.created_by_username)
          .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
      </div>
      <span style={{ fontSize: 12, color: 'var(--text2)' }}>
        {txn.created_by_username}
      </span>
    </div>
  ) : (
    <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>
  )}
</td>
                      <td>
                        <div className="flex gap-8">
                          <button className="btn btn-ghost btn-sm" title="View details" onClick={() => setSelected(txn)}>
                            <FiEye />
                          </button>
                          {txn.status === 'PENDING' && (
                            <button className="btn btn-ghost btn-sm" title="Check status" onClick={() => checkStatus(txn.merchant_transaction_id)}>
                              <FiRotateCw />
                            </button>
                          )}
                          {txn.payment_link && (
                            <a className="btn btn-ghost btn-sm" href={txn.payment_link} target="_blank" rel="noreferrer" title="Open link">
                              <FiExternalLink />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between" style={{ padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13, color: 'var(--text3)' }}>
                Page {filters.page} of {pages} — {total} records
              </span>
              <div className="flex gap-8">
                <button className="btn btn-outline btn-sm" disabled={filters.page <= 1}
                  onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>← Prev</button>
                <button className="btn btn-outline btn-sm" disabled={filters.page >= pages}
                  onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selected && <TxnModal txn={selected} onClose={() => setSelected(null)} />}
    </Layout>
  );
}

function TxnModal({ txn, onClose }) {
  const rows = [
    ['Merchant Transaction ID', txn.merchant_transaction_id],
    ['PhonePe Transaction ID',  txn.phonepe_transaction_id || '—'],
    ['Customer Name',           txn.customer_name],
    ['Phone',                   txn.customer_phone],
    ['Email',                   txn.customer_email || '—'],
    ['Amount',                  `₹${parseFloat(txn.amount).toLocaleString('en-IN')}`],
    ['Amount (Paise)',           txn.amount_paise],
    ['Event Name',              txn.event_name || '—'],
    ['Payment Type',            txn.payment_type],
    ['Status',                  txn.status],
    ['Webhook Verified',        txn.webhook_verified ? 'Yes ✓' : 'No'],
    ['Email Sent',              !txn.customer_email ? 'N/A (no email)' : txn.email_sent ? 'Yes ✓' : 'No'],
    ['Description',             txn.description || '—'],
    ['Created By', txn.created_by_name
  ? `${txn.created_by_name} (@${txn.created_by_username})`
  : txn.created_by_username || '—'],
    ['Created At',              new Date(txn.created_at).toLocaleString('en-IN')],
    ['Updated At',              txn.updated_at ? new Date(txn.updated_at).toLocaleString('en-IN') : '—'],
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border2)',
        borderRadius: 16, padding: 28, width: '100%', maxWidth: 560,
        maxHeight: '85vh', overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-16">
          <h3 style={{ fontFamily: 'Syne,sans-serif', fontSize: 17, fontWeight: 700 }}>Transaction Details</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><FiX /></button>
        </div>

        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '9px 0', color: 'var(--text3)', width: '45%', verticalAlign: 'top' }}>{label}</td>
                <td style={{
                  padding: '9px 0 9px 12px', color: 'var(--text)', fontWeight: 500, wordBreak: 'break-all',
                  color: label === 'Email Sent' && value === 'Yes ✓' ? 'var(--success)'
                       : label === 'Event Name' && value !== '—' ? 'var(--primary)'
                       : 'var(--text)',
                }}>
                  {value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {txn.payment_link && (
          <div style={{ marginTop: 16 }}>
            <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>Payment Link</p>
            <a href={txn.payment_link} target="_blank" rel="noreferrer"
              style={{ fontSize: 12, color: 'var(--primary)', wordBreak: 'break-all' }}>
              {txn.payment_link}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}